// Copyright 2023, Durable
import {
  Clear as ClearIcon,
  ClearAll as ClearAllIcon,
  Notifications as NotificationsIcon
} from "@mui/icons-material";
import {
  Badge,
  Button,
  Card,
  ClickAwayListener,
  Collapse,
  IconButton,
  Tooltip
} from "@mui/material";
import React, { useState } from "react";
import { useNotificationContext } from "../../NotificationProvider";
import { Action } from "@durable/durable-client";

/**
 * Trim a string with ...
 */
export function ellipsisString(string: string, length: number) {
  return string.substring(0, length) + "...";
}

/**
 * A dropdown for viewing and managing notifications, as well as seeing if you
 * have new unread notifications.
 */
function NotificationDropdown({ open }: { open: boolean }) {
  /**
   * A single element of the Notification Dropdown
   */
  function NotificationItem({
    title,
    body,
    unread,
    actions,
    id
  }: {
    title: string;
    body?: string;
    unread?: boolean;
    actions?: Array<Action>;
    id?: string;
  }) {
    if (title.length > 30) {
      title = ellipsisString(title, 30);
    }
    if (body?.length > 100) {
      body = ellipsisString(body, 100);
    }

    /**
     * If provided an ID, delete that notification. If not, delete all
     * notifications.
     */
    function clearNotification(idToClear?: string) {
      if (idToClear) {
        notificationSystem.deleteNotification(idToClear);
      } else {
        notificationSystem.allNotifications?.map((notification) =>
          notificationSystem.deleteNotification(notification.id)
        );
      }
    }

    return (
      <div>
        <div className="flex w-full justify-between">
          <div className="text-lg font-semibold">{title}</div>
          <Tooltip
            title={
              id != null
                ? "Dismiss Notification"
                : "Dismiss all notifications"
            }
          >
            <IconButton size="small" onClick={() => clearNotification(id)}>
              {id != null ? (
                <ClearIcon fontSize="small" className="opacity-50" />
              ) : (
                <ClearAllIcon fontSize="small" className="opacity-50" />
              )}
            </IconButton>
          </Tooltip>
        </div>
        <div className="flex items-center text-sm">
          {unread && (
            <div
              className={`x-1 mr-2 h-4 w-1
            rounded-full bg-durableTealDark-300`}
            />
          )}
          {body}
        </div>
        <div className="flex w-full justify-end">
          {actions?.map((action, i) => (
            <Tooltip title={action.tooltip} key={i}>
              <Button
                href={
                  "link" in action.action
                    ? action.action.link
                    : action.action.url
                }
                color={action.variant == "primary" ? "primary" : "inherit"}
                variant="text"
                size="small"
              >
                {action.text}
              </Button>
            </Tooltip>
          ))}
        </div>
        <div className="mt-3 mb-4 h-[1px] w-full bg-coolGrey-500/50" />
      </div>
    );
  }

  const notificationSystem = useNotificationContext();
  return (
    <div className="pointer-events-none absolute top-0 right-0">
      <Collapse in={open} unmountOnExit>
        <Card
          className={`pointer-events-auto  mt-12
        w-72 p-5 shadow-lg`}
        >
          <div>
            <NotificationItem
              title={`Your Notifications (${
                notificationSystem.allNotifications?.length ?? 0
              })`}
            />
          </div>
          <div className="max-h-[36rem] overflow-scroll">
            {notificationSystem.allNotifications?.length > 0
              ? notificationSystem.allNotifications?.map((notification) => (
                  <div key={notification.id}>
                    <NotificationItem
                      title={notification.title}
                      body={notification.summary}
                      unread={!notification.read}
                      actions={notification.actions}
                      id={notification.id}
                    />
                  </div>
                ))
              : "You have no notifications."}
          </div>
        </Card>
      </Collapse>
    </div>
  );
}

/**
 * The button to open the notification dropdown.
 */
function NotificationButton() {
  const [open, setOpen] = useState(false);
  const notificationSystem = useNotificationContext();

  return (
    <div className="relative">
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <div>
          <IconButton
            className="text-coolGrey-500 dark:text-coolGrey-600"
            onClick={() => {
              setOpen(!open);
            }}
          >
            <Badge
              badgeContent={notificationSystem.unreadNotifications?.length}
              overlap="circular"
              color="primary"
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <NotificationDropdown open={open} />
        </div>
      </ClickAwayListener>
    </div>
  );
}

export { NotificationButton };
